/**
 * Lindy Product Form Handler
 *
 * Handles product form submission events for Lindy-specific functionality
 * without modifying the core product-form.js file.
 *
 * Dispatches custom events to product cards to show the overlay when items are added.
 */

import { CartAddEvent } from '@theme/events';

class LindyProductFormHandler {
  constructor() {
    this.init();
  }

  init() {
    // Listen for cart add events at the document level
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Handles cart add events and dispatches overlay event to product cards
   * @param {CustomEvent} event - The cart add event
   */
  #handleCartAdd = (event) => {
    if (!(event.target instanceof Element)) return;

    // Check if the add came from a product form (handles both product-form and product-form-component)
    const productForm = event.target?.closest('product-form') || event.target?.closest('product-form-component');
    if (!productForm) return;

    // Find the parent product card or lindy product card
    const productCard = productForm.closest('product-card') || productForm.closest('.lindy-product-card');
    if (!productCard) return;

    // Get quantity and product ID from the form
    const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    const formData = new FormData(form);
    const quantity = Number(formData.get('quantity')) || Number(productForm.dataset.quantityDefault) || 1;
    const productId = productForm.dataset.productId;

    // Dispatch custom event to product card for overlay display
    productCard.dispatchEvent(new CustomEvent('product-card:added', {
      bubbles: true,
      detail: {
        itemCount: quantity,
        productId: productId,
      }
    }));
  };

  destroy() {
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }
}

// Initialize the handler
const lindyProductFormHandler = new LindyProductFormHandler();

// Export for potential cleanup
export default lindyProductFormHandler;
